import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Folder, FolderOpen, File, FileText, Image as ImageIcon, ChevronLeft, ChevronRight,
  ArrowUp, Search, Upload, Download, Trash2, Copy, Pencil, Plus, X, RefreshCw,
  LayoutGrid, List, Eye, FolderPlus, MoreVertical, Home, Database, Cloud, ExternalLink,
  HardDrive, CloudOff,
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import { api } from '../../services/api';
import type { DriveFile } from '../../types';

interface FileManagerProps {
  projectId?: string;
  initialFolderId?: string;
}

// Workspace (server disk) entry shape returned by /api/projects/:id/files
interface WsEntry {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: WsEntry[];
}

// Normalize the workspace tree into the same row shape the Drive view uses.
function flattenWsTree(nodes: WsEntry[], prefix = ''): Array<{ id: string; name: string; mimeType: string; wsPath: string }> {
  const out: Array<{ id: string; name: string; mimeType: string; wsPath: string }> = [];
  for (const n of nodes || []) {
    const wsPath = prefix ? `${prefix}/${n.path}` : n.path;
    if (n.type === 'directory') {
      out.push({ id: `wsdir:${wsPath}`, name: n.name, mimeType: 'application/vnd.google-apps.folder', wsPath });
      if (n.children) out.push(...flattenWsTree(n.children, wsPath));
    } else {
      out.push({ id: `ws:${wsPath}`, name: n.name, mimeType: 'application/octet-stream', wsPath });
    }
  }
  return out;
}

export function FileManagerApp({ initialFolderId }: FileManagerProps) {
  const loadFiles = useStore((s) => s.loadFiles);
  const files = useStore((s) => s.files);
  const currentFolderId = useStore((s) => s.currentFolderId);
  const loadingFiles = useStore((s) => s.loadingFiles);
  const addNotification = useStore((s) => s.addNotification);
  const currentProject = useStore((s) => s.currentProject);
  const driveReauthNeeded = useStore((s) => s.driveReauthNeeded);

  // 'workspace' = project files on the server disk (what the terminal/editor see)
  // 'drive'     = your Google Drive (what the Files app showed before)
  const [mode, setMode] = useState<'workspace' | 'drive'>(
    initialFolderId ? 'drive' : 'workspace'
  );

  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DriveFile[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: 'MyCloudProjects' },
  ]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: DriveFile } | null>(null);
  const [previewFile, setPreviewFile] = useState<DriveFile | null>(null);
  const [previewContent, setPreviewContent] = useState('');
  const [downloadProgress, setDownloadProgress] = useState<string | null>(null);
  const [wsBusy, setWsBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // ---- Workspace state ----
  const [wsFiles, setWsFiles] = useState<ReturnType<typeof flattenWsTree>>([]);
  const [wsLoading, setWsLoading] = useState(false);

  // ---- Drive helpers (unchanged behaviour) ----
  useEffect(() => {
    if (mode === 'drive') {
      const target = initialFolderId || currentProject?.googleDriveId;
      loadFiles(target || undefined);
    }
  }, [mode, initialFolderId, currentProject?.googleDriveId]);

  // ---- Workspace loading ----
  const loadWorkspace = useCallback(async () => {
    if (!currentProject) {
      setWsFiles([]);
      return;
    }
    setWsLoading(true);
    try {
      const res = await api.getProjectFiles(currentProject.id);
      setWsFiles(flattenWsTree(res.tree || []));
    } catch {
      setWsFiles([]);
      addNotification('Error', 'Workspace files load nahi hue', 'error');
    } finally {
      setWsLoading(false);
    }
  }, [currentProject, addNotification]);

  useEffect(() => {
    if (mode === 'workspace') loadWorkspace();
  }, [mode, loadWorkspace]);

  const navigateTo = (folderId: string | null, name: string) => {
    setBreadcrumb((prev) => [...prev, { id: folderId, name }]);
    loadFiles(folderId || undefined);
    setSearchResults(null);
    setSearchQuery('');
  };

  const goHome = () => {
    setBreadcrumb([{ id: null, name: 'MyCloudProjects' }]);
    setSearchResults(null);
    loadFiles(undefined);
  };

  const goBack = (index: number) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);
    const target = newBreadcrumb[newBreadcrumb.length - 1];
    loadFiles(target.id || undefined);
    setSearchResults(null);
    setSearchQuery('');
  };

  const handleSearch = async (q: string) => {
    setSearchQuery(q);
    if (mode !== 'drive') return; // workspace search: simple client filter below
    if (!q.trim()) {
      setSearchResults(null);
      loadFiles(currentFolderId || undefined);
      return;
    }
    setSearching(true);
    try {
      const res = await api.searchFiles(q);
      setSearchResults(res.files || res);
    } catch {
      addNotification('Error', 'Search failed', 'error');
    } finally {
      setSearching(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      if (mode === 'workspace') {
        if (!currentProject) return;
        const parent = wsDirContext();
        await api.createProjectFolder(currentProject.id, parent ? `${parent}/${newFolderName.trim()}` : newFolderName.trim());
        setShowNewFolder(false);
        setNewFolderName('');
        loadWorkspace();
      } else {
        await api.createFolder(newFolderName.trim(), currentFolderId || undefined);
        setShowNewFolder(false);
        setNewFolderName('');
        loadFiles(currentFolderId || undefined);
      }
      addNotification('Success', 'Folder created', 'success');
    } catch {
      addNotification('Error', 'Failed to create folder', 'error');
    }
  };

  // The wsPath of the currently "open" workspace directory (grid items use it).
  const wsDirContext = (): string | null => {
    return null; // flat view: new folders/files are created at project root
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.split(',')[1] : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const uploadWsFile = async (file: File) => {
    if (!currentProject) return;
    setDownloadProgress(`Uploading ${file.name}...`);
    try {
      const content = await readFileAsBase64(file);
      await fetch(`/api/projects/${currentProject.id}/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: file.name, content, isBase64: true }),
      }).then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Upload failed');
      });
      await loadWorkspace();
      addNotification('Success', `Uploaded ${file.name}`, 'success');
    } catch {
      addNotification('Error', `Failed to upload ${file.name}`, 'error');
    } finally {
      setDownloadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mode === 'workspace') return uploadWsFile(file);
    setDownloadProgress(`Uploading ${file.name}...`);
    try {
      const content = await readFileAsBase64(file);
      await api.uploadFile(file.name, content, currentFolderId || undefined, file.type, true);
      loadFiles(currentFolderId || undefined);
      addNotification('Success', `Uploaded ${file.name}`, 'success');
    } catch {
      addNotification('Error', `Failed to upload ${file.name}`, 'error');
    } finally {
      setDownloadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (file: File) => {
    if (mode === 'workspace') return uploadWsFile(file);
    setDownloadProgress(`Uploading ${file.name}...`);
    try {
      const content = await readFileAsBase64(file);
      await api.uploadFile(file.name, content, currentFolderId || undefined, file.type, true);
      loadFiles(currentFolderId || undefined);
      addNotification('Success', `Uploaded ${file.name}`, 'success');
    } catch {
      addNotification('Error', 'Upload failed', 'error');
    } finally {
      setDownloadProgress(null);
    }
  };

  const handleDownload = async (file: DriveFile) => {
    setDownloadProgress(`Downloading ${file.name}...`);
    try {
      let blob: Blob;
      if (mode === 'workspace' && file.id.startsWith('ws:')) {
        const res = await fetch(`/api/projects/${currentProject!.id}/file?path=${encodeURIComponent(file.id.slice(3))}`, { credentials: 'include' });
        if (!res.ok) throw new Error();
        blob = await res.blob();
      } else {
        blob = await api.downloadFileBlob(file.id);
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
      addNotification('Success', `Downloaded ${file.name}`, 'success');
    } catch {
      addNotification('Error', 'Download failed', 'error');
    } finally {
      setDownloadProgress(null);
    }
  };

  const openInGoogle = (file: DriveFile) => {
    const id = file.id;
    let url = `https://drive.google.com/file/d/${id}/view`;
    if (file.mimeType === 'application/vnd.google-apps.document') {
      url = `https://docs.google.com/document/d/${id}/edit`;
    } else if (file.mimeType === 'application/vnd.google-apps.spreadsheet') {
      url = `https://docs.google.com/spreadsheets/d/${id}/edit`;
    } else if (file.mimeType === 'application/vnd.google-apps.presentation') {
      url = `https://docs.google.com/presentation/d/${id}/edit`;
    } else if (file.mimeType === 'application/vnd.google-apps.folder') {
      url = `https://drive.google.com/drive/folders/${id}`;
    }
    window.open(url, '_blank', 'noopener');
  };

  const handleDelete = async (file: DriveFile) => {
    if (!confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    try {
      if (mode === 'workspace' && file.id.startsWith('ws:')) {
        await api.deleteProjectFile(currentProject!.id, file.id.slice(3));
        loadWorkspace();
      } else if (mode === 'workspace' && file.id.startsWith('wsdir:')) {
        await api.deleteProjectFile(currentProject!.id, file.id.slice(7));
        loadWorkspace();
      } else {
        await api.deleteFile(file.id);
        loadFiles(currentFolderId || undefined);
        if (searchResults) {
          setSearchResults((prev) => prev?.filter((f) => f.id !== file.id) || null);
        }
      }
      addNotification('Info', `Deleted ${file.name}`, 'info');
    } catch {
      addNotification('Error', 'Delete failed', 'error');
    }
  };

  const handleRename = async (file: DriveFile) => {
    const newName = prompt('New name:', file.name);
    if (!newName || newName === file.name) return;
    try {
      if (mode === 'workspace' && currentProject) {
        const oldPath = file.id.startsWith('ws:') ? file.id.slice(3) : file.id.slice(7);
        const parts = oldPath.split('/');
        parts[parts.length - 1] = newName;
        const res = await fetch(`/api/projects/${currentProject.id}/rename`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from: oldPath, to: parts.join('/') }),
        });
        if (!res.ok) throw new Error();
        loadWorkspace();
      } else {
        await api.renameFile(file.id, newName);
        setPreviewFile(null);
        setContextMenu(null);
        loadFiles(currentFolderId || undefined);
        if (searchResults) {
          setSearchResults((prev) => prev?.map((f) => (f.id === file.id ? { ...f, name: newName } : f)) || null);
        }
      }
      addNotification('Success', 'Renamed', 'success');
    } catch {
      addNotification('Error', 'Rename failed', 'error');
    }
  };

  const handleCopy = async (file: DriveFile) => {
    try {
      await api.copyFile(file.id, currentFolderId || undefined);
      loadFiles(currentFolderId || undefined);
      addNotification('Success', `Copied ${file.name}`, 'success');
    } catch {
      addNotification('Error', 'Copy failed', 'error');
    }
  };

  const handlePreview = async (file: DriveFile) => {
    if (file.mimeType === 'application/vnd.google-apps.folder') {
      if (mode === 'workspace') return; // flat view
      navigateTo(file.id, file.name);
      return;
    }
    setPreviewFile(file);
    try {
      if (mode === 'workspace' && file.id.startsWith('ws:') && currentProject) {
        const res = await api.getProjectFile(currentProject.id, file.id.slice(3));
        setPreviewContent(res.content || '');
      } else {
        const content = await api.downloadFile(file.id);
        setPreviewContent(content);
      }
    } catch {
      setPreviewContent('Unable to preview this file. Download it instead.');
    }
  };

  const openProjectFolder = () => {
    if (currentProject?.googleDriveId) {
      setBreadcrumb([{ id: null, name: 'MyCloudProjects' }, { id: currentProject.googleDriveId, name: currentProject.name }]);
      loadFiles(currentProject.googleDriveId);
      setSearchResults(null);
    } else {
      goHome();
    }
  };

  const displayedFiles: DriveFile[] = mode === 'workspace'
    ? wsFiles as any
    : searchResults || files;

  const wsFilter = (f: DriveFile) => {
    if (!searchQuery.trim()) return true;
    return f.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
  };

  const getFileIcon = (file: DriveFile) => {
    if (file.mimeType === 'application/vnd.google-apps.folder') {
      return <Folder className="w-10 h-10 text-amber-400" />;
    }
    if (file.mimeType?.startsWith('image/')) {
      return <ImageIcon className="w-10 h-10 text-pink-400" />;
    }
    if (file.mimeType?.includes('text') || /\.(js|ts|tsx|jsx|json|html|css|md|py|sh|yml|yaml)$/.test(file.name)) {
      return <FileText className="w-10 h-10 text-blue-400" />;
    }
    return <File className="w-10 h-10 text-slate-400" />;
  };

  // Auto-pull from Drive into the workspace when opening workspace mode with
  // a fresh/empty project (once per mount, only when workspace is empty).
  const autoPullDone = useRef(false);
  useEffect(() => {
    if (mode !== 'workspace' || !currentProject || autoPullDone.current) return;
    autoPullDone.current = true;
    (async () => {
      try {
        const res = await api.getProjectFiles(currentProject.id);
        const flat = flattenWsTree(res.tree || []);
        const hasRealFiles = flat.some((f) => f.id.startsWith('ws:'));
        const driveFolder = currentProject.googleDriveId;
        if (!hasRealFiles && driveFolder) {
          setWsBusy(true);
          await api.syncProjectDown(currentProject.id);
          await loadWorkspace();
          addNotification('Synced', 'Project files pulled from Google Drive', 'success');
        }
      } catch {
        // Non-fatal: user can pull manually via taskbar buttons
      } finally {
        setWsBusy(false);
      }
    })();
  }, [mode, currentProject, loadWorkspace, addNotification]);

  return (
    <div className="h-full flex flex-col bg-desktop-surface2" onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={async (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = Array.from(e.dataTransfer.files)[0];
      if (file) handleDrop(file);
    }}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-desktop-border bg-desktop-surface shrink-0">
        {mode === 'drive' && (
          <>
            <button onClick={goHome} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70" title="MyCloudProjects root">
              <Home className="w-3.5 h-3.5" />
            </button>
            {currentProject && (
              <button onClick={openProjectFolder} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-accent" title="Current project folder">
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
            )}
            <div className="flex items-center gap-0.5 flex-1">
              {breadcrumb.map((crumb, i) => (
                <div key={i} className="flex items-center">
                  <button
                    onClick={() => goBack(i)}
                    className="text-xs px-1.5 py-1 rounded hover:bg-desktop-hover text-desktop-text/80 transition-colors"
                  >
                    {crumb.name}
                  </button>
                  {i < breadcrumb.length - 1 && <ChevronRight className="w-3 h-3 text-desktop-text/30" />}
                </div>
              ))}
            </div>
          </>
        )}
        {mode === 'workspace' && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <HardDrive className="w-3.5 h-3.5 text-desktop-text/60 shrink-0" />
            <span className="text-xs text-desktop-text/70 truncate">
              {currentProject ? `workspace/${currentProject.name}` : 'No project open'}
            </span>
            {!currentProject && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-desktop-warning/20 text-desktop-warning">Open a project first</span>
            )}
          </div>
        )}
        {/* Mode switch */}
        <div className="flex items-center rounded-md border border-desktop-border overflow-hidden shrink-0">
          <button
            onClick={() => setMode('workspace')}
            className={`px-2 py-1.5 text-[11px] flex items-center gap-1 ${mode === 'workspace' ? 'bg-desktop-accent/20 text-desktop-accent' : 'text-desktop-text/60 hover:bg-desktop-hover'} transition-colors`}
            title="Project files on the server (terminal/editor inhi ko dekhte hain)"
          >
            <HardDrive className="w-3.5 h-3.5" /> Workspace
          </button>
          <button
            onClick={() => setMode('drive')}
            className={`px-2 py-1.5 text-[11px] flex items-center gap-1 ${mode === 'drive' ? 'bg-desktop-accent/20 text-desktop-accent' : 'text-desktop-text/60 hover:bg-desktop-hover'} transition-colors`}
            title="Google Drive files"
          >
            <Cloud className="w-3.5 h-3.5" /> Drive
          </button>
        </div>
        <div className="w-px h-5 bg-desktop-border mx-1" />
        {mode === 'drive' && (
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-desktop-text/40" />
            <input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search Drive..."
              className="w-44 pl-7 pr-2 py-1.5 text-xs bg-desktop-surface2 border border-desktop-border rounded-md text-desktop-text placeholder:text-desktop-text/40 focus:outline-none focus:border-desktop-accent"
            />
          </div>
        )}
        {mode === 'workspace' && (
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-desktop-text/40" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter files..."
              className="w-44 pl-7 pr-2 py-1.5 text-xs bg-desktop-surface2 border border-desktop-border rounded-md text-desktop-text placeholder:text-desktop-text/40 focus:outline-none focus:border-desktop-accent"
            />
          </div>
        )}
        <div className="w-px h-5 bg-desktop-border mx-1" />
        <div className="flex items-center rounded-md border border-desktop-border overflow-hidden">
          <button
            onClick={() => setView('grid')}
            className={`px-2 py-1.5 ${view === 'grid' ? 'bg-desktop-accent/20 text-desktop-accent' : 'text-desktop-text/60 hover:bg-desktop-hover'} transition-colors`}
            title="Grid view"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-2 py-1.5 ${view === 'list' ? 'bg-desktop-accent/20 text-desktop-accent' : 'text-desktop-text/60 hover:bg-desktop-hover'} transition-colors`}
            title="List view"
          >
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="w-px h-5 bg-desktop-border mx-1" />
        {showNewFolder ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setShowNewFolder(false); }}
              placeholder="Folder name"
              className="w-28 px-2 py-1 text-xs bg-desktop-surface2 border border-desktop-accent rounded-md text-desktop-text focus:outline-none"
            />
            <button onClick={createFolder} className="px-2 py-1 text-xs bg-desktop-accent text-white rounded-md hover:opacity-90">OK</button>
            <button onClick={() => setShowNewFolder(false)} className="px-1.5 py-1 text-xs text-desktop-text/60 hover:text-desktop-text"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewFolder(true)}
            className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md hover:bg-desktop-hover text-desktop-text/70 transition-colors"
            title="New folder"
          >
            <FolderPlus className="w-3.5 h-3.5" />
            <span className="hidden md:inline">New Folder</span>
          </button>
        )}
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md hover:bg-desktop-hover text-desktop-text/70 transition-colors"
          title="Upload file"
        >
          <Upload className="w-3.5 h-3.5" />
          <span className="hidden md:inline">Upload</span>
        </button>
        <button
          onClick={() => (mode === 'workspace' ? loadWorkspace() : loadFiles(currentFolderId || undefined))}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/70"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${wsBusy || loadingFiles ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {mode === 'workspace' && (
        <div className="mx-2 mt-2 px-3 py-2 text-xs bg-desktop-accent/10 border border-desktop-accent/30 rounded-lg flex items-center justify-between gap-2 shrink-0">
          <span className="text-desktop-text/80 flex items-center gap-2 min-w-0">
            <Cloud className="w-3.5 h-3.5 shrink-0 text-desktop-accent" />
            <span className="truncate">Workspace files server disk par hain. Google Drive se sync karo:</span>
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <button
              disabled={!currentProject || wsBusy}
              onClick={async () => {
                if (!currentProject) return;
                setWsBusy(true);
                try {
                  await api.syncProjectDown(currentProject.id);
                  await loadWorkspace();
                  addNotification('Synced', 'Pulled from Google Drive', 'success');
                } catch { addNotification('Error', 'Pull failed', 'error'); }
                finally { setWsBusy(false); }
              }}
              className="px-2 py-1 rounded-md bg-desktop-accent text-white text-[11px] hover:opacity-90 disabled:opacity-40"
              title="Pull from Google Drive"
            >
              <Download className="w-3 h-3 inline mr-1 -mt-0.5" />Pull
            </button>
            <button
              disabled={!currentProject || wsBusy}
              onClick={async () => {
                if (!currentProject) return;
                setWsBusy(true);
                try {
                  const r = await api.syncProjectUp(currentProject.id);
                  addNotification('Synced', `Pushed to Google Drive (${(r as any)?.uploaded ?? '?'} files)`, 'success');
                } catch { addNotification('Error', 'Push failed', 'error'); }
                finally { setWsBusy(false); }
              }}
              className="px-2 py-1 rounded-md bg-desktop-accent text-white text-[11px] hover:opacity-90 disabled:opacity-40"
              title="Push to Google Drive"
            >
              <Upload className="w-3 h-3 inline mr-1 -mt-0.5" />Push
            </button>
          </div>
        </div>
      )}

      {driveReauthNeeded && (
        <div className="mx-2 mt-2 px-3 py-2 text-xs bg-desktop-accent/15 border border-desktop-accent/40 rounded-lg flex items-center justify-between shrink-0">
          <span className="text-desktop-text flex items-center gap-2"><CloudOff className="w-3.5 h-3.5" /> Google authorization expired.</span>
          <button
            onClick={async () => {
              try {
                const { url } = await api.getGoogleAuthUrl();
                window.location.href = url;
              } catch {}
            }}
            className="px-2.5 py-1 bg-desktop-accent text-white rounded-md hover:opacity-90"
          >
            Reconnect
          </button>
        </div>
      )}
      {dragOver && (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-desktop-accent m-2 rounded-xl">
          <div className="text-center">
            <Upload className="w-12 h-12 text-desktop-accent mx-auto mb-2" />
            <p className="text-sm text-desktop-accent">Drop to upload</p>
          </div>
        </div>
      )}

      {!dragOver && (
        <>
          {/* Breadcrumb for search mode */}
          {mode === 'drive' && searchResults && (
            <div className="px-3 py-1.5 text-xs text-desktop-text/60 bg-desktop-surface border-b border-desktop-border shrink-0">
              {searchResults.length} result(s) for "{searchQuery}"
            </div>
          )}

          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-3">
            {(mode === 'workspace' ? wsLoading || wsBusy : loadingFiles || searching) ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-desktop-border border-t-desktop-accent rounded-full animate-spin" />
              </div>
            ) : mode === 'workspace' && !currentProject ? (
              <div className="flex flex-col items-center justify-center h-full text-desktop-text/40 gap-2">
                <Folder className="w-14 h-14" />
                <p className="text-sm">Open a project to see its workspace files</p>
              </div>
            ) : displayedFiles.filter(wsFilter).length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-desktop-text/40 gap-2">
                <Folder className="w-14 h-14" />
                <p className="text-sm">
                  {mode === 'workspace' ? 'Workspace is empty — Pull from Drive ya terminal me files banao' : 'This folder is empty'}
                </p>
                {mode === 'drive' && !currentFolderId && (
                  <p className="text-xs">Click "New Folder" to create one</p>
                )}
              </div>
            ) : view === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {displayedFiles.filter(wsFilter).map((file) => (
                  <div
                    key={file.id}
                    onClick={() => handlePreview(file)}
                    onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, file }); }}
                    className="group relative flex flex-col items-center justify-center gap-2 p-3 rounded-lg border border-transparent hover:border-desktop-border hover:bg-desktop-hover/50 cursor-pointer transition-colors"
                  >
                    {getFileIcon(file)}
                    <span className={`text-xs text-center line-clamp-2 break-all ${file.mimeType === 'application/vnd.google-apps.folder' ? 'text-desktop-text/90' : 'text-desktop-text/70'}`}>
                      {file.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-desktop-text/40 border-b border-desktop-border">
                    <th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5 hidden sm:table-cell">Path</th>
                    <th className="px-2 py-1.5 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedFiles.filter(wsFilter).map((file) => (
                    <tr
                      key={file.id}
                      onClick={() => handlePreview(file)}
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, file }); }}
                      className="border-b border-desktop-border/30 hover:bg-desktop-hover/40 cursor-pointer transition-colors"
                    >
                      <td className="px-2 py-2 flex items-center gap-2 text-sm text-desktop-text">
                        <span className="shrink-0">
                          {file.mimeType === 'application/vnd.google-apps.folder'
                            ? <Folder className="w-4 h-4 text-amber-400" />
                            : <File className="w-4 h-4 text-slate-400" />}
                        </span>
                        <span className="truncate max-w-56">{file.name}</span>
                      </td>
                      <td className="px-2 py-2 text-xs text-desktop-text/60 hidden sm:table-cell">
                        {file.id.startsWith('ws:') || file.id.startsWith('wsdir:')
                          ? file.id.split(':').slice(1).join(':').split('/').slice(0, -1).join('/') || '/'
                          : file.modifiedTime ? new Date(file.modifiedTime).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleDownload(file)} title="Download" className="p-1 rounded hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text"><Download className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleRename(file)} title="Rename" className="p-1 rounded hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text"><Pencil className="w-3.5 h-3.5" /></button>
                          {mode === 'drive' && (
                            <button onClick={() => handleCopy(file)} title="Copy here" className="p-1 rounded hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text"><Copy className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => handleDelete(file)} title="Delete" className="p-1 rounded hover:bg-red-600/20 text-desktop-text/60 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1 border-t border-desktop-border bg-desktop-surface shrink-0">
        <span className="text-[10px] text-desktop-text/50">
          {loadingFiles || wsLoading ? 'Loading...' : `${displayedFiles.filter(wsFilter).length} item(s)`}
        </span>
        {mode === 'workspace' ? (
          <span className="text-[10px] text-desktop-accent flex items-center gap-1"><HardDrive className="w-3 h-3" /> Server workspace</span>
        ) : currentFolderId ? (
          <span className="text-[10px] text-desktop-accent">Google Drive</span>
        ) : (
          <span className="text-[10px] text-desktop-accent flex items-center gap-1"><Cloud className="w-3 h-3" /> MyCloudProjects</span>
        )}
      </div>

      {downloadProgress && (
        <div className="fixed bottom-16 right-4 z-[9500] glass rounded-xl px-4 py-3 text-sm text-desktop-text shadow-xl">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-desktop-accent animate-spin" />
            {downloadProgress}
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-[9400]" onClick={() => setContextMenu(null)} />
          <div className="fixed z-[9500] glass rounded-lg p-1 shadow-2xl min-w-44" style={{ top: contextMenu.y, left: contextMenu.x }}>
            <div className="px-3 py-1.5 text-xs text-desktop-text/50 border-b border-desktop-border truncate max-w-52">{contextMenu.file.name}</div>
            {contextMenu.file.mimeType === 'application/vnd.google-apps.folder' && mode === 'drive' ? (
              <button onClick={() => { navigateTo(contextMenu.file.id, contextMenu.file.name); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
                <FolderOpen className="w-4 h-4" /> Open
              </button>
            ) : (
              <button onClick={() => { handlePreview(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
                <Eye className="w-4 h-4" /> Preview
              </button>
            )}
            {mode === 'drive' && (
              <button onClick={() => { openInGoogle(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
                <ExternalLink className="w-4 h-4" /> Open in Google
              </button>
            )}
            <button onClick={() => { handleDownload(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={() => { handleRename(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
              <Pencil className="w-4 h-4" /> Rename
            </button>
            {mode === 'drive' && (
              <button onClick={() => { handleCopy(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-desktop-hover text-desktop-text">
                <Copy className="w-4 h-4" /> Copy to here
              </button>
            )}
            <div className="h-px bg-desktop-border my-1" />
            <button onClick={() => { handleDelete(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-red-600/20 text-red-400">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </>
      )}

      {/* Preview modal */}
      {previewFile && (
        <div className="fixed inset-0 z-[9600] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="animate-window-open glass rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-desktop-border shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                {previewFile.mimeType === 'application/vnd.google-apps.folder'
                  ? <Folder className="w-4 h-4 text-amber-400" />
                  : <File className="w-4 h-4 text-slate-400" />}
                <span className="text-sm text-desktop-text truncate">{previewFile.name}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => handleDownload(previewFile)} className="px-2.5 py-1 text-xs bg-desktop-accent text-white rounded-md hover:opacity-90">Download</button>
                <button onClick={() => { setPreviewFile(null); setPreviewContent(''); }} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-600 hover:text-white text-desktop-text/70">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-auto min-h-0">
              {previewFile.mimeType?.startsWith('image/') ? (
                <img
                  src={`${import.meta.env.VITE_API_URL || ''}/api/drive/files/${previewFile.id}/raw?t=${Date.now()}`}
                  alt={previewFile.name}
                  className="max-w-full max-h-[60vh] mx-auto rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : previewFile.mimeType?.startsWith('audio/') ? (
                <audio controls src={`${import.meta.env.VITE_API_URL || ''}/api/drive/files/${previewFile.id}/raw?t=${Date.now()}`} className="w-full" />
              ) : previewFile.mimeType?.startsWith('video/') ? (
                <video controls src={`${import.meta.env.VITE_API_URL || ''}/api/drive/files/${previewFile.id}/raw?t=${Date.now()}`} className="w-full max-h-[60vh]" />
              ) : (
                <pre className="text-xs font-mono text-desktop-text whitespace-pre-wrap break-words">
                  {previewContent.slice(0, 100000)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
